import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import reportsService, {
    PublicReportPoint,
    PublicReportResponse,
} from '../api/reportsService';
import CanvasGrid from '../components/CanvasGrid';
import { UseConnectSocket } from '../hooks/useConnectSocket';
import { formatNumberWithUnit, formatReportDate } from '../utils/userReport';
import './user-report.css';

type DisplayPoint = {
    x: number;
    y: number;
    y1: number;
    isRPeaks: boolean;
    marker?: string | null;
    sdnn?: number | string;
    rmssd?: number | string;
    pnn20?: number | string;
    pnn50?: number | string;
};

const mapPoints = (points: PublicReportPoint[], startX = 0): DisplayPoint[] =>
    points.map((point, index) => {
        return {
            x: startX + index,
            y: Number(point.value) || 0,
            y1: Number(point.photo) || 0,
            isRPeaks: false,
            marker: point.marker,
            sdnn: point.sdnn,
            rmssd: point.rmssd,
            pnn20: point.pnn20,
            pnn50: point.pnn50,
        };
    });

const genderLabel = (gender?: number | null) => {
    if (gender === 1) return 'Male';
    if (gender === 0) return 'Female';
    return '—';
};

const User = () => {
    const { search } = useLocation();
    const query = useMemo(() => new URLSearchParams(search), [search]);
    const value = query.get('value') ?? '';
    const pin = query.get('pin');
    const { data: socketData } = UseConnectSocket(value);

    const [report, setReport] = useState<PublicReportResponse | null>(null);
    const [points, setPoints] = useState<DisplayPoint[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [pulse, setPulse] = useState<number | string>(80);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError(null);

        if (!value) {
            setError('Report link is invalid');
            setLoading(false);
            return () => {
                active = false;
            };
        }

        reportsService
            .getPoints({ value, pin })
            .then(({ data }) => {
                if (!active) return;
                setReport(data);
                setPoints(mapPoints(data.points ?? []));
            })
            .catch((requestError) => {
                if (!active) return;
                setError(
                    requestError?.response?.data?.message ??
                        requestError?.message ??
                        'Unable to load report',
                );
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => {
            active = false;
        };
    }, [value, pin]);

    useEffect(() => {
        const incoming = socketData?.points as PublicReportPoint[] | undefined;
        if (!incoming?.length) return;

        setPoints((current) => {
            const startX = current[current.length - 1]?.x ?? 0;
            return [...current, ...mapPoints(incoming, startX)];
        });
    }, [socketData]);

    const user = report?.user;
    const meta = report?.reportMeta;

    if (loading) {
        return <div className="user-report-status">Loading report…</div>;
    }
    if (error) {
        return <div className="user-report-status user-report-error">{error}</div>;
    }

    const headerItems = [
        { label: 'TEST NAME', value: meta?.testName || '—', className: 'test-name' },
        { label: 'DATE', value: formatReportDate(meta?.created_at), className: 'date' },
        { label: 'GENDER', value: genderLabel(user?.gender) },
        { label: 'AGE', value: formatNumberWithUnit(user?.age, 'years') },
        { label: 'HEIGHT', value: formatNumberWithUnit(user?.height, 'cm', 1) },
        { label: 'WEIGHT', value: formatNumberWithUnit(user?.weight, 'kg', 1) },
        { label: 'EMAIL', value: user?.email || '—', className: 'email' },
    ];

    return (
        <main className="user-report-page">
            <header className="user-report-header">
                {headerItems.map((item) => (
                    <div
                        className={`user-report-header-item ${item.className ?? ''}`}
                        key={item.label}
                    >
                        <span>{item.label}:</span>
                        <strong title={item.value}>{item.value}</strong>
                    </div>
                ))}
            </header>

            <div className="user-report-body">
                <aside className="user-report-metrics">
                    <div className="user-report-metric pulse">
                        <span className="user-report-metric-label">
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z" />
                            </svg>
                            PULSE:
                        </span>
                        <div>
                            <strong>{pulse}</strong>
                            <span>bpm</span>
                        </div>
                    </div>
                    <div className="user-report-metric spo2">
                        <span className="user-report-metric-label">
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M4 8h8.5a2.5 2.5 0 1 0-2.3-3.5M4 12h14a2.5 2.5 0 1 1-2.3 3.5M4 16h5" />
                            </svg>
                            SPO2:
                        </span>
                        <div>
                            <strong>98</strong>
                            <span>%</span>
                        </div>
                    </div>
                </aside>

                <section className="user-report-chart-area">
                    <CanvasGrid
                        points={points}
                        setPulsUser={(value: number | string) => setPulse(value)}
                        variant="userReport"
                    />
                </section>
            </div>
        </main>
    );
};

export { User };
